import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicNav } from "../components/public/PublicNav";
import { PublicFooter } from "../components/public/PublicFooter";

type Section = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

const SECTIONS: Section[] = [
  {
    id: "getting-started",
    label: "Getting Started",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    id: "dashboard",
    label: "Dashboard Overview",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    id: "menu",
    label: "Menu Management",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    id: "hours",
    label: "Operating Hours",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: "orders",
    label: "Managing Orders",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    id: "staff",
    label: "Staff & Roles",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
];

function StepBadge({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-100 text-brand-600 text-xs font-bold shrink-0">
      {n}
    </span>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-gray-50 rounded-xl border border-gray-100 px-5 py-4 text-sm text-gray-700 font-mono overflow-x-auto whitespace-pre-wrap">
      {children}
    </pre>
  );
}

function InfoBox({ color, label, children }: { color: string; label: string; children: React.ReactNode }) {
  return (
    <div className={`rounded-xl p-5 border-l-4 ${color}`}>
      <p className="text-gray-600 text-sm">
        <span className="font-semibold">{label}:</span>{" "}
        {children}
      </p>
    </div>
  );
}

const CONTENT: Record<string, React.ReactNode> = {
  "getting-started": (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Getting Started with Cravbot</h2>
        <p className="text-gray-500 leading-relaxed">
          Cravbot connects your restaurant's ordering workflow to an AI engine that your customers
          can interact with through any platform. Here's how to go from zero to taking live orders.
        </p>
      </div>

      <div className="space-y-6">
        {[
          {
            title: "Contact the Cravbot Team",
            desc: "Cravbot is available by invitation only. Fill out our contact form with your restaurant details, and our team will reach out to create your account.",
          },
          {
            title: "Receive Your Credentials",
            desc: "Once approved, you'll receive your Business ID (tenant slug), an owner account (email + password), and your API keys for integration.",
          },
          {
            title: "Set Up Your Menu",
            desc: "Log in to your dashboard and go to the Menu section. Add your categories, items, prices, and descriptions. The AI uses this to answer customer queries accurately.",
          },
          {
            title: "Configure Business Hours",
            desc: "Go to Business Settings → Hours and set your opening/closing times for each day. The bot will automatically decline orders when you're closed.",
          },
          {
            title: "Integrate the API",
            desc: "Your development team integrates the Cravbot API into your website, mobile app, or any chat platform. Customers can then start ordering through your existing channels.",
          },
          {
            title: "Monitor & Manage",
            desc: "Use the dashboard to track orders, view statistics, manage your team, and update menu availability in real time.",
          },
        ].map(({ title, desc }, i) => (
          <div key={title} className="flex gap-4">
            <StepBadge n={i + 1} />
            <div>
              <h3 className="text-gray-800 font-semibold text-sm mb-1">{title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      <InfoBox color="border-brand-400 bg-brand-50/50" label="Tip">
        Seed your menu with accurate item names, descriptions, and prices before going live. The AI
        uses this data to answer every customer question correctly.
      </InfoBox>
    </div>
  ),

  dashboard: (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Dashboard Overview</h2>
        <p className="text-gray-500 leading-relaxed">
          Your Cravbot dashboard is the control center for your restaurant. Here's what each section does.
        </p>
      </div>

      <div className="space-y-4">
        {[
          {
            section: "Overview",
            color: "border-brand-400 bg-brand-50/30",
            labelColor: "text-brand-600",
            desc: "Shows key KPIs: total revenue, total orders, average order value, and top-selling items. Use the time filter (7d / 30d / 90d) to change the chart range. All figures are based on confirmed (punched) orders.",
          },
          {
            section: "Orders",
            color: "border-purple-400 bg-purple-50/30",
            labelColor: "text-purple-600",
            desc: "A real-time list of all orders. Click any row to expand and see full item details, customer phone, delivery address, and payment method. Approve or Reject pending orders directly from this page when your tenant uses the approval flow.",
          },
          {
            section: "Menu",
            color: "border-cyan-400 bg-cyan-50/30",
            labelColor: "text-cyan-600",
            desc: "Add, edit, and remove menu items. Mark items as out-of-stock instantly — the bot stops offering them until you re-enable them. Supports fixed-price and size-based pricing.",
          },
          {
            section: "Business",
            color: "border-yellow-400 bg-yellow-50/30",
            labelColor: "text-yellow-600",
            desc: "Configure operating hours, toggle delivery availability, set announcements, and manage business holidays.",
          },
          {
            section: "Staff",
            color: "border-pink-400 bg-pink-50/30",
            labelColor: "text-pink-600",
            desc: "Invite team members with specific roles: Owner (full access), Manager (orders + menu), Staff (orders only). Each member has their own login.",
          },
        ].map(({ section, color, labelColor, desc }) => (
          <div key={section} className={`rounded-xl p-5 border-l-4 ${color}`}>
            <h3 className={`font-bold text-sm mb-1.5 ${labelColor}`}>{section}</h3>
            <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  ),

  menu: (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Menu Management</h2>
        <p className="text-gray-500 leading-relaxed">
          Keep your menu accurate and up-to-date. Changes reflect immediately in the AI bot.
        </p>
      </div>

      <div className="space-y-5">
        <h3 className="text-gray-800 font-semibold">Adding a New Item</h3>
        <div className="space-y-3">
          {[
            'Click "Add Item" in the Menu section.',
            "Fill in: Name, Description, Category, and Price.",
            'For size-based items (e.g. drinks), toggle "Size-based pricing" and add Small/Medium/Large prices.',
            "Mark the item as available (default is on).",
            'Click "Save" — changes are live immediately.',
          ].map((step, i) => (
            <div key={i} className="flex gap-3 text-sm text-gray-500">
              <StepBadge n={i + 1} />
              <span className="pt-0.5">{step}</span>
            </div>
          ))}
        </div>

        <h3 className="text-gray-800 font-semibold mt-6">Marking an Item Out of Stock</h3>
        <p className="text-gray-500 text-sm">
          Find the item in your menu list and toggle the availability switch off. The bot will
          immediately stop offering this item and inform customers if they ask for it.
        </p>

        <h3 className="text-gray-800 font-semibold mt-6">Popular Items</h3>
        <p className="text-gray-500 text-sm">
          Mark up to 5 items as "Popular" to highlight them in the bot's recommendations. The AI
          will proactively suggest these when customers ask for recommendations.
        </p>

        <InfoBox color="border-yellow-400 bg-yellow-50/30" label="Note">
          Item names in the Popular Items list must exactly match the names in your menu.
          The AI uses exact name matching to present these items.
        </InfoBox>
      </div>
    </div>
  ),

  hours: (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Operating Hours</h2>
        <p className="text-gray-500 leading-relaxed">
          Set precise operating hours per day. The bot automatically handles open/closed states.
        </p>
      </div>

      <div className="space-y-5">
        <h3 className="text-gray-800 font-semibold">Setting Hours</h3>
        <p className="text-gray-500 text-sm">Navigate to Business → Operating Hours. For each day of the week you can:</p>
        <ul className="space-y-2">
          {[
            "Toggle the day open or closed.",
            "Set an opening time and closing time using the time picker.",
            "For overnight hours (e.g. 5:00 PM – 3:00 AM), simply set the close time earlier than the open time — Cravbot detects this automatically and shows a 🌙 badge.",
          ].map((item, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-gray-500">
              <svg className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {item}
            </li>
          ))}
        </ul>

        <h3 className="text-gray-800 font-semibold mt-6">How the Bot Uses Hours</h3>
        <p className="text-gray-500 text-sm leading-relaxed">
          When a customer messages outside of your configured hours, the bot immediately
          responds with a polite closed message including today's opening time. The AI is
          not called at all — saving your token budget.
        </p>

        <CodeBlock>{`Example closed message:
Sorry, Yummy Boy is currently closed.
We're open today from 5:00 PM to 3:00 AM.
Please try again during our opening hours!`}</CodeBlock>

        <InfoBox color="border-cyan-400 bg-cyan-50/30" label="Overnight hours">
          If your restaurant is open 5 PM – 3 AM, set open = 17:00 and close = 03:00. The system
          automatically detects cross-midnight schedules.
        </InfoBox>
      </div>
    </div>
  ),

  orders: (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Managing Orders</h2>
        <p className="text-gray-500 leading-relaxed">
          Every order placed through the AI bot appears in your Orders page in real time.
        </p>
      </div>

      <div className="space-y-5">
        <h3 className="text-gray-800 font-semibold">Order Lifecycle</h3>
        <div className="flex flex-wrap gap-2 text-xs font-medium">
          {["PENDING_APPROVAL", "→", "APPROVED", "→", "PUNCHED", "→", "COMPLETED"].map((s) =>
            s === "→" ? (
              <span key={Math.random()} className="text-gray-300 self-center">{s}</span>
            ) : (
              <span key={s} className="bg-gray-100 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg">
                {s}
              </span>
            )
          )}
        </div>

        <div className="space-y-4 mt-2">
          {[
            { status: "PENDING_APPROVAL", color: "text-yellow-600", desc: "Order is waiting for your review in the dashboard." },
            { status: "APPROVED", color: "text-blue-600", desc: "You've approved the order. It will be forwarded to your POS system." },
            { status: "PUNCHED", color: "text-green-600", desc: "Order successfully submitted to your POS. This is the final confirmed state counted in your stats." },
            { status: "REJECTED", color: "text-red-500", desc: "Order was rejected by owner/manager. The customer is notified automatically." },
          ].map(({ status, color, desc }) => (
            <div key={status} className="flex gap-3">
              <span className={`font-bold text-xs mt-0.5 w-36 shrink-0 ${color}`}>{status}</span>
              <p className="text-gray-500 text-sm">{desc}</p>
            </div>
          ))}
        </div>

        <h3 className="text-gray-800 font-semibold mt-6">Approving / Rejecting Orders</h3>
        <p className="text-gray-500 text-sm">
          When an order needs manual review, it arrives with Approve (✓) and Reject (✕)
          buttons. Only owners and managers can perform this action. Click the order row to
          expand full details before deciding.
        </p>
      </div>
    </div>
  ),

  staff: (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Staff & Roles</h2>
        <p className="text-gray-500 leading-relaxed">Manage your team with role-based access control.</p>
      </div>

      <div className="space-y-5">
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-sm bg-white">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="py-3 px-5 text-left text-gray-500 font-medium">Role</th>
                <th className="py-3 px-5 text-left text-gray-500 font-medium">Access</th>
              </tr>
            </thead>
            <tbody>
              {[
                { role: "Owner", color: "text-brand-600", access: "Full access to all dashboard features, billing, and staff management." },
                { role: "Manager", color: "text-purple-600", access: "Orders, menu, business hours, and staff viewing. Cannot manage billing or invite owners." },
                { role: "Staff", color: "text-cyan-600", access: "View and manage orders only. Cannot edit menu, hours, or staff." },
              ].map(({ role, color, access }) => (
                <tr key={role} className="border-b border-gray-50">
                  <td className={`py-3.5 px-5 font-semibold ${color}`}>{role}</td>
                  <td className="py-3.5 px-5 text-gray-500">{access}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-gray-800 font-semibold mt-4">Inviting a Staff Member</h3>
        <div className="space-y-3">
          {[
            "Go to Staff in the sidebar.",
            'Click "Invite Staff Member".',
            "Enter their name, email, and select a role.",
            'Click "Send Invite" — they receive an email with setup instructions.',
          ].map((step, i) => (
            <div key={i} className="flex gap-3 text-sm text-gray-500">
              <StepBadge n={i + 1} />
              <span className="pt-0.5">{step}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  ),
};

export function Guides() {
  const [active, setActive] = useState("getting-started");

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden transition-colors duration-200">
      <PublicNav />

      {/* Header */}
      <section className="pt-28 pb-12 relative overflow-hidden bg-gradient-to-b from-purple-50/50 to-white">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-purple-600 text-sm font-semibold tracking-widest uppercase mb-4"
          >
            Documentation
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900"
          >
            Guides &{" "}
            <span className="gradient-text-cyan">Documentation</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-4 text-gray-500 max-w-lg mx-auto"
          >
            Everything you need to set up, configure, and get the most out of Cravbot.
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="lg:w-56 shrink-0">
              <nav className="sticky top-24 space-y-1">
                {SECTIONS.map(({ id, label, icon }) => (
                  <button
                    key={id}
                    onClick={() => setActive(id)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-colors ${
                      active === id
                        ? "bg-brand-50 text-brand-700 border border-brand-100"
                        : "text-gray-500 hover:text-gray-800:text-gray-200 hover:bg-gray-50:bg-gray-800"
                    }`}
                  >
                    <span className={active === id ? "text-brand-500" : "text-gray-400"}>{icon}</span>
                    {label}
                  </button>
                ))}

                <div className="pt-4 mt-4 border-t border-gray-100">
                  <Link
                    to="/contact"
                    className="flex items-center gap-2 text-xs text-gray-400 hover:text-brand-500 transition-colors px-4"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    Still have questions?
                  </Link>
                </div>
              </nav>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-8">
                {CONTENT[active]}
              </div>
            </main>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
